// Password page JavaScript

// Countdown Timer
class CountdownTimer {
  constructor(element) {
    this.element = element;
    // Parse date string - handle "YYYY-MM-DD HH:MM:SS" format
    const dateString = element.dataset.targetDate;
    if (!dateString) {
      console.error('CountdownTimer: No target date provided');
      return;
    }
    
    // Parse "YYYY-MM-DD HH:MM:SS" format manually to ensure correct local time parsing
    const parts = dateString.trim().split(' ');
    if (parts.length !== 2) {
      console.error('CountdownTimer: Invalid date format, expected "YYYY-MM-DD HH:MM:SS"');
      return;
    }
    
    const datePart = parts[0].split('-');
    const timePart = parts[1].split(':');
    
    if (datePart.length !== 3 || timePart.length < 2) {
      console.error('CountdownTimer: Invalid date/time format');
      return;
    }
    
    const year = parseInt(datePart[0], 10);
    const month = parseInt(datePart[1], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(datePart[2], 10);
    const hour = parseInt(timePart[0], 10);
    const minute = parseInt(timePart[1], 10);
    const second = timePart.length > 2 ? parseInt(timePart[2], 10) : 0;
    
    // Create date in local timezone
    this.targetDate = new Date(year, month, day, hour, minute, second);
    
    // Validate the date was parsed correctly
    if (isNaN(this.targetDate.getTime())) {
      console.error('CountdownTimer: Invalid date values');
      return;
    }
    
    this.hideDays = element.dataset.hideDays === 'true';
    this.daysEl = element.querySelector('[data-days]');
    this.hoursEl = element.querySelector('[data-hours]');
    this.minutesEl = element.querySelector('[data-minutes]');
    this.secondsEl = element.querySelector('[data-seconds]');
    this.interval = null;
    this.init();
  }

  formatTime(value) {
    return value < 10 ? `0${value}` : value.toString();
  }

  update() {
    const now = new Date();
    const remaining = this.targetDate - now;

    if (remaining < 0) {
      if (this.daysEl) this.daysEl.textContent = '0';
      if (this.hoursEl) this.hoursEl.textContent = '00';
      if (this.minutesEl) this.minutesEl.textContent = '00';
      if (this.secondsEl) this.secondsEl.textContent = '00';
      return;
    }

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    if (this.daysEl && !this.hideDays) {
      this.daysEl.textContent = days.toString();
    }
    if (this.hoursEl) {
      // If days are hidden, convert days to hours and add to hours count
      if (this.hideDays) {
        const totalHours = (days * 24) + hours;
        this.hoursEl.textContent = totalHours.toString();
      } else {
        this.hoursEl.textContent = this.formatTime(hours);
      }
    }
    if (this.minutesEl) {
      this.minutesEl.textContent = this.formatTime(minutes);
    }
    if (this.secondsEl) {
      this.secondsEl.textContent = this.formatTime(seconds);
    }
  }

  init() {
    this.update();
    this.interval = setInterval(() => this.update(), 1000);
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

// Glitchy Marquee
class GlitchyMarquee {
  constructor(element) {
    this.element = element;
    this.textElements = element.querySelectorAll('[data-marquee-text]');
    this.chars = 'АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ0123456789•';
    this.glitchingWords = new Set();
    this.scrambledChars = new Map();
    this.currentlyGlitching = new Set();
    this.scrambleIntervals = new Set();
    this.wordRefs = new Map();
    this.words = [];
    this.init();
  }

  init() {
    if (!this.textElements.length) return;
    
    const text = this.textElements[0].textContent;
    this.words = text.split(/(\s+)/);
    
    // Create word elements and store refs
    this.textElements.forEach((textEl) => {
      const fragment = document.createDocumentFragment();
      this.words.forEach((word, index) => {
        const span = document.createElement('span');
        span.className = 'glitchy-marquee__word';
        span.textContent = word;
        span.dataset.wordIndex = index;
        fragment.appendChild(span);
        if (!this.wordRefs.has(index)) {
          this.wordRefs.set(index, []);
        }
        this.wordRefs.get(index).push(span);
      });
      textEl.innerHTML = '';
      textEl.appendChild(fragment);
    });

    // Start glitch interval
    this.startGlitchInterval();
  }

  startGlitchInterval() {
    this.glitchInterval = setInterval(() => {
      if (!this.element) return;

      const containerRect = this.element.getBoundingClientRect();
      const visibleIndices = [];

      this.wordRefs.forEach((spans, index) => {
        if (spans.length === 0) return;
        const span = spans[0];
        const rect = span.getBoundingClientRect();
        const padding = 100;

        if (
          rect.left < containerRect.right + padding &&
          rect.right > containerRect.left - padding &&
          rect.top < containerRect.bottom &&
          rect.bottom > containerRect.top
        ) {
          const word = span.textContent.trim();
          if (word && word.length > 0 && !this.currentlyGlitching.has(index)) {
            visibleIndices.push(index);
          }
        }
      });

      if (visibleIndices.length === 0) return;

      const numWordsToGlitch = Math.min(Math.floor(Math.random() * 4) + 2, visibleIndices.length);
      const shuffled = [...visibleIndices].sort(() => Math.random() - 0.5);
      const newGlitching = new Set();

      for (let i = 0; i < numWordsToGlitch; i++) {
        const wordIndex = shuffled[i];
        newGlitching.add(wordIndex);
        this.currentlyGlitching.add(wordIndex);

        const spans = this.wordRefs.get(wordIndex);
        if (!spans || spans.length === 0) continue;

        const word = spans[0].textContent;
        const scrambled = word.split('').map((char) => {
          if (char === ' ' || char === '•') return char;
          return this.chars[Math.floor(Math.random() * this.chars.length)];
        });

        this.scrambledChars.set(wordIndex, scrambled);
        spans.forEach((span) => {
          span.textContent = scrambled.join('');
          span.dataset.glitching = 'true';
        });
      }

      if (newGlitching.size === 0) return;

      this.glitchingWords = new Set([...this.glitchingWords, ...newGlitching]);

      const scrambleInterval = setInterval(() => {
        newGlitching.forEach((wordIndex) => {
          const spans = this.wordRefs.get(wordIndex);
          if (!spans || spans.length === 0) return;

          const word = spans[0].textContent;
          const scrambled = word.split('').map((char) => {
            if (char === ' ' || char === '•') return char;
            return this.chars[Math.floor(Math.random() * this.chars.length)];
          });

          spans.forEach((span) => {
            span.textContent = scrambled.join('');
          });
        });
      }, 30);

      this.scrambleIntervals.add(scrambleInterval);

      setTimeout(() => {
        clearInterval(scrambleInterval);
        this.scrambleIntervals.delete(scrambleInterval);
        newGlitching.forEach((idx) => {
          this.currentlyGlitching.delete(idx);
          this.glitchingWords.delete(idx);
          this.scrambledChars.delete(idx);
          const spans = this.wordRefs.get(idx);
          if (spans && this.words[idx]) {
            const originalWord = this.words[idx];
            spans.forEach((span) => {
              span.textContent = originalWord;
              delete span.dataset.glitching;
            });
          }
        });
      }, 200 + Math.random() * 200);
    }, 500);
  }

  destroy() {
    if (this.glitchInterval) {
      clearInterval(this.glitchInterval);
    }
    this.scrambleIntervals.forEach((interval) => clearInterval(interval));
  }
}

// Depixelation effect
class Depixelation {
  constructor(introComplete, duration = 1200) {
    this.introComplete = introComplete;
    this.duration = duration;
    this.progress = 0;
    this.startTime = null;
    this.animationFrame = null;
    this.values = {
      blurStdDev: 5.3,
      tileSize: 13,
      displacementScale: 37,
    };
    this.init();
  }

  init() {
    if (!this.introComplete) {
      this.progress = 0;
      return;
    }

    this.startTime = Date.now();
    this.animate();
  }

  animate() {
    const elapsed = Date.now() - this.startTime;
    this.progress = Math.min(elapsed / this.duration, 1);

    const eased = 1 - Math.pow(1 - this.progress, 3);
    const ratio = 1 - eased;

    this.values = {
      blurStdDev: 5.3 * ratio,
      tileSize: 13 * ratio,
      displacementScale: 37 * ratio,
    };

    if (this.progress < 1) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
    }
  }

  getValues() {
    return this.values;
  }

  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }
}

// Lookbook Carousel using Swiper
class LookbookCarousel {
  constructor(element) {
    this.element = element;
    this.swiperElement = element.querySelector('[data-lookbook-swiper]');
    this.swiper = null;
    this.imageElements = [];
    this.init();
  }

  init() {
    if (!this.swiperElement) return;

    // Wait for Swiper to be available
    if (typeof Swiper === 'undefined') {
      // Retry after a short delay
      setTimeout(() => this.init(), 50);
      return;
    }

    // Get all image wrappers for pixelation
    this.imageElements = Array.from(this.swiperElement.querySelectorAll('[data-image-wrapper]'));

    // Initialize Swiper
    this.swiper = new Swiper(this.swiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 15,
      grabCursor: true,
      resistance: true,
      resistanceRatio: 0,
      speed: 300,
      centeredSlides: false,
      breakpoints: {
        768: {
          slidesPerView: 1,
          spaceBetween: 15,
        },
      },
      on: {
        init: () => {
          this.setupPixelation();
        },
        slideChange: () => {
          // Re-check pixelation on slide change
          this.setupPixelation();
        },
        resize: () => {
          // Update pixelation observers on resize
          this.setupPixelation();
        },
      },
    });

    // Setup pixelation after Swiper is initialized
    setTimeout(() => {
      this.setupPixelation();
    }, 100);
  }

  setupPixelation() {
    const observerOptions = {
      root: this.swiperElement,
      rootMargin: '0px',
      threshold: 0.1,
    };

    this.imageElements.forEach((imgWrapper) => {
      // Skip if already set up
      if (imgWrapper._pixelationObserver) return;

      const depixelation = new Depixelation(false);
      const img = imgWrapper.querySelector('img');
      const filterWrapper = imgWrapper.querySelector('[data-filter-id]');
      const filterId = filterWrapper?.dataset.filterId || img?.dataset.filterId;
      const filter = document.getElementById(filterId);
      
      if (!filter || !img) return;

      const blurEl = filter.querySelector('[data-blur]');
      const displacementEl = filter.querySelector('[data-displacement]');
      const tileSizeEl = filter.querySelector('[data-tile-size]');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            depixelation.introComplete = true;
            depixelation.init();
            
            const updateFilter = () => {
              const values = depixelation.getValues();
              if (blurEl) blurEl.setAttribute('stdDeviation', values.blurStdDev);
              if (displacementEl) displacementEl.setAttribute('scale', values.displacementScale);
              if (tileSizeEl) {
                const tileSize = Math.max(1, values.tileSize);
                tileSizeEl.setAttribute('width', tileSize);
                tileSizeEl.setAttribute('height', tileSize);
              }
              
              const hasPixelation = values.blurStdDev > 0.01 || values.tileSize > 0.01 || values.displacementScale > 0.01;
              img.style.filter = hasPixelation ? `url(#${filterId})` : 'none';
              
              if (depixelation.progress < 1) {
                requestAnimationFrame(updateFilter);
              }
            };
            updateFilter();
          } else {
            depixelation.introComplete = false;
            depixelation.progress = 0;
            depixelation.values = { blurStdDev: 5.3, tileSize: 13, displacementScale: 37 };
            if (blurEl) blurEl.setAttribute('stdDeviation', '5.3');
            if (displacementEl) displacementEl.setAttribute('scale', '37');
            if (tileSizeEl) {
              tileSizeEl.setAttribute('width', '13');
              tileSizeEl.setAttribute('height', '13');
            }
            if (img) {
              img.style.filter = `url(#${filterId})`;
            }
          }
        });
      }, observerOptions);

      observer.observe(imgWrapper);
      imgWrapper._pixelationObserver = observer;
      imgWrapper._depixelation = depixelation;
    });
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    // Clean up observers and depixelation instances
    this.imageElements.forEach((imgWrapper) => {
      if (imgWrapper._pixelationObserver) {
        imgWrapper._pixelationObserver.disconnect();
        delete imgWrapper._pixelationObserver;
      }
      if (imgWrapper._depixelation) {
        imgWrapper._depixelation.destroy();
        delete imgWrapper._depixelation;
      }
    });
  }
}

// Lookbook Product Grid
class LookbookGrid {
  constructor(sectionElement) {
    this.sectionElement = sectionElement;
    this.overlay = sectionElement.querySelector('[data-lookbook-grid]');
    this.trigger = sectionElement.querySelector('[data-lookbook-trigger]');
    this.closeBtn = sectionElement.querySelector('[data-lookbook-grid-close]');
    this.gridContainer = sectionElement.querySelector('[data-lookbook-grid-container]');
    this.productItems = [];
    this.init();
  }

  init() {
    if (!this.overlay) return;

    // Get all product items
    this.productItems = Array.from(this.gridContainer?.querySelectorAll('[data-product-id]') || []);

    // Set up event listeners (support both click and touch for mobile)
    if (this.trigger) {
      const handleTriggerClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Don't open if button is disabled or no products
        if (this.trigger.disabled || this.productItems.length === 0) {
          return;
        }
        this.open();
      };
      
      this.trigger.addEventListener('click', handleTriggerClick);
      // Add touch support for mobile
      this.trigger.addEventListener('touchend', (e) => {
        e.preventDefault();
        handleTriggerClick(e);
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Close on backdrop click
    const backdrop = this.overlay.querySelector('.lookbook-grid-overlay__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close());
    }

    // Set up product click handlers (support both click and touch for mobile)
    this.productItems.forEach((item) => {
      let touchStartPos = null;
      let touchMoved = false;
      let touchHandled = false;
      const TAP_THRESHOLD = 10; // pixels - movement beyond this is considered a scroll
      
      const openProduct = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productId = item.dataset.productId;
        const productHandle = item.dataset.productHandle;
        
        console.log('[Lookbook Grid] Product card clicked:', {
          productId,
          productHandle,
          eventType: e.type,
          timestamp: new Date().toISOString()
        });
        
        if (productId && productHandle) {
          this.openProductDetail(productId, productHandle);
        } else {
          console.warn('[Lookbook Grid] Missing product data:', { productId, productHandle });
        }
      };
      
      // Track touch start position
      item.addEventListener('touchstart', (e) => {
        touchMoved = false;
        touchHandled = false;
        const touch = e.touches[0];
        if (touch) {
          touchStartPos = {
            x: touch.clientX,
            y: touch.clientY
          };
        }
      }, { passive: true });
      
      // Track touch movement - use passive listener for better scroll performance
      item.addEventListener('touchmove', (e) => {
        if (!touchStartPos) return;
        
        const touch = e.touches[0];
        if (touch) {
          const deltaX = Math.abs(touch.clientX - touchStartPos.x);
          const deltaY = Math.abs(touch.clientY - touchStartPos.y);
          
          // If movement exceeds threshold, treat as scroll
          if (deltaX > TAP_THRESHOLD || deltaY > TAP_THRESHOLD) {
            touchMoved = true;
            touchStartPos = null;
          }
        }
      }, { passive: true });
      
      // Handle touch end - only fire if it was a tap (not a scroll)
      item.addEventListener('touchend', (e) => {
        // Only handle if we didn't scroll and touch start position exists
        if (touchStartPos && !touchMoved) {
          touchHandled = true;
          e.preventDefault();
          e.stopPropagation();
          openProduct(e);
        }
        // Reset state after a delay to allow click handler to check touchHandled
        setTimeout(() => {
          touchStartPos = null;
          touchMoved = false;
          touchHandled = false;
        }, 300);
      }, { passive: false });
      
      // Click handler for desktop and as fallback for mobile
      item.addEventListener('click', (e) => {
        // Prevent double-firing on mobile devices that fire both touch and click
        if (touchHandled) {
          return;
        }
        // Desktop or mobile fallback - handle click
        openProduct(e);
      });
    });

    // Close on Escape key
    this.handleEscape = (e) => {
      if (e.key === 'Escape' && !this.overlay.hasAttribute('hidden')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.handleEscape);
  }

  open() {
    if (!this.overlay) return;
    // Apply blur immediately before showing
    const backdrop = this.overlay.querySelector('.lookbook-grid-overlay__backdrop');
    if (backdrop) {
      backdrop.style.backdropFilter = 'blur(12px)';
      backdrop.style.webkitBackdropFilter = 'blur(12px)';
    }
    this.overlay.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    document.documentElement.classList.add('modal-open');
    // Trigger animation
    requestAnimationFrame(() => {
      this.overlay.classList.add('lookbook-grid-overlay--open');
    });
  }

  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove('lookbook-grid-overlay--open');
    setTimeout(() => {
      this.overlay.setAttribute('hidden', '');
      // Check if product detail modal is open - if so, don't remove modal-open class
      const productModal = document.querySelector('[data-product-detail-modal]:not([hidden])');
      if (!productModal) {
        // Only remove modal-open class if no other modals are open
        const openModals = document.querySelectorAll('.lookbook-grid-overlay:not([hidden]), .product-detail-modal:not([hidden]), [data-password-form-modal]:not([hidden])');
        if (openModals.length === 0) {
          document.body.style.overflow = '';
          document.body.classList.remove('modal-open');
          document.documentElement.classList.remove('modal-open');
        }
      }
    }, 300);
  }

  openProductDetail(productId, productHandle) {
    console.log('[Lookbook Grid] Opening product detail:', {
      productId,
      productHandle,
      timestamp: new Date().toISOString()
    });
    
    // Store reference to grid for reopening
    this.currentProductId = productId;
    this.currentProductHandle = productHandle;
    // Dispatch event immediately - don't wait for grid to close
    const event = new CustomEvent('lookbook:open-product', {
      detail: { productId, productHandle, gridInstance: this }
    });
    document.dispatchEvent(event);
    // Close the grid overlay after a brief delay to allow modal to start opening
    setTimeout(() => {
      this.close();
    }, 50);
  }

  destroy() {
    if (this.handleEscape) {
      document.removeEventListener('keydown', this.handleEscape);
    }
  }
}

// Product Detail Modal
class ProductDetailModal {
  constructor(sectionElement) {
    this.sectionElement = sectionElement;
    this.modal = sectionElement.querySelector('[data-product-detail-modal]');
    this.backdrop = sectionElement.querySelector('[data-product-detail-backdrop]');
    this.content = sectionElement.querySelector('[data-product-detail-content]');
    this.closeBtn = sectionElement.querySelector('[data-product-detail-close]');
    this.mainImage = sectionElement.querySelector('[data-product-detail-main-image]');
    this.thumbnails = sectionElement.querySelector('[data-product-detail-thumbnails]');
    this.title = sectionElement.querySelector('[data-product-detail-title]');
    this.price = sectionElement.querySelector('[data-product-detail-price]');
    this.description = sectionElement.querySelector('[data-product-detail-description]');
    this.currentProduct = null;
    this.gridInstance = null;
    
    // Load product data from JSON script tag
    this.productsData = this.loadProductsData();
    
    this.init();
  }

  loadProductsData() {
    const jsonScript = this.sectionElement.querySelector('[data-products-json]');
    if (!jsonScript) {
      console.warn('[Product Detail Modal] No products JSON data found');
      return [];
    }
    
    try {
      const products = JSON.parse(jsonScript.textContent);
      console.log('[Product Detail Modal] Loaded products data from JSON:', {
        count: products.length,
        productIds: products.map(p => p.id)
      });
      return products;
    } catch (error) {
      console.error('[Product Detail Modal] Error parsing products JSON:', error);
      return [];
    }
  }

  init() {
    if (!this.modal) return;

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Close on backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Listen for product open events
    document.addEventListener('lookbook:open-product', (e) => {
      const { productId, productHandle, gridInstance } = e.detail;
      console.log('[Product Detail Modal] Received lookbook:open-product event:', {
        productId,
        productHandle,
        hasGridInstance: !!gridInstance,
        timestamp: new Date().toISOString()
      });
      this.gridInstance = gridInstance;
      this.loadProduct(productId, productHandle);
    });

    // Close on Escape key
    this.handleEscape = (e) => {
      if (e.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.handleEscape);
  }

  loadProduct(productId, productHandle) {
    try {
      // Find product in the pre-loaded JSON data
      const productIdNum = parseInt(productId, 10);
      let product = this.productsData.find(p => 
        p.id === productIdNum || p.handle === productHandle
      );
      
      if (product) {
        console.log('[Product Detail Modal] Product loaded from JSON data:', {
          productId: product.id,
          title: product.title,
          handle: product.handle,
          variantsCount: product.variants?.length || 0,
          imagesCount: product.images?.length || 0
        });
        
        this.currentProduct = product;
        this.renderProduct(product);
        this.open();
      } else {
        throw new Error(`Product not found in JSON data for ID: ${productId}, Handle: ${productHandle}`);
      }
    } catch (error) {
      console.error('[Product Detail Modal] Error loading product:', {
        error: error.message,
        stack: error.stack,
        productId,
        productHandle,
        availableProducts: this.productsData.map(p => ({ id: p.id, handle: p.handle })),
        timestamp: new Date().toISOString()
      });
    }
  }

  renderProduct(product) {
    // Set title
    if (this.title) {
      this.title.textContent = product.title;
    }

    // Set price - Shopify product JSON uses variants[0].price in cents
    if (this.price) {
      const price = product.variants && product.variants.length > 0 
        ? product.variants[0].price 
        : 0;
      this.price.textContent = this.formatMoney(price);
    }

    // Set description
    if (this.description) {
      this.description.innerHTML = product.description || '';
    }

    // Set main image
    if (this.mainImage && product.images && product.images.length > 0) {
      const mainImg = product.images[0];
      this.mainImage.innerHTML = `
        <img 
          src="${mainImg}" 
          alt="${product.title}"
          loading="eager"
        />
      `;
    }

    // Set thumbnails (limit to first 3 images)
    if (this.thumbnails && product.images && product.images.length > 1) {
      // Limit to first 3 images
      const thumbnailImages = product.images.slice(0, 5);
      this.thumbnails.innerHTML = thumbnailImages.map((img, index) => `
        <button 
          type="button"
          class="product-detail-modal__thumbnail ${index === 0 ? 'product-detail-modal__thumbnail--active' : ''}"
          data-thumbnail-index="${index}"
          aria-label="View image ${index + 1}"
        >
          <img src="${img}" alt="${product.title} - Image ${index + 1}" />
        </button>
      `).join('');

      // Add thumbnail click handlers
      const thumbnailButtons = this.thumbnails.querySelectorAll('[data-thumbnail-index]');
      thumbnailButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = parseInt(btn.dataset.thumbnailIndex);
          this.changeMainImage(index, product.images);
          
          // Update active state
          thumbnailButtons.forEach((b) => b.classList.remove('product-detail-modal__thumbnail--active'));
          btn.classList.add('product-detail-modal__thumbnail--active');
        });
      });
    } else if (this.thumbnails) {
      this.thumbnails.innerHTML = '';
    }
  }

  changeMainImage(index, images) {
    if (!this.mainImage || !images || index >= images.length) return;
    const img = images[index];
    this.mainImage.innerHTML = `
      <img 
        src="${img}" 
        alt="${this.currentProduct?.title || ''}"
        loading="eager"
      />
    `;
  }

  formatMoney(cents) {
    // Format money from cents - Shopify product JSON returns price in cents
    // Try to use Shopify's money format if available, otherwise fallback
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    // Fallback: basic formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  open() {
    if (!this.modal) return;
    // Apply blur immediately before showing
    if (this.backdrop) {
      this.backdrop.style.backdropFilter = 'blur(12px)';
      this.backdrop.style.webkitBackdropFilter = 'blur(12px)';
    }
    // Remove hidden attribute first
    this.modal.removeAttribute('hidden');
    // Ensure modal is visible and on top
    this.modal.style.display = 'flex';
    this.modal.style.zIndex = '150';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    document.documentElement.classList.add('modal-open');
    // Trigger animation
    requestAnimationFrame(() => {
      this.modal.classList.add('product-detail-modal--open');
    });
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('product-detail-modal--open');
    setTimeout(() => {
      this.modal.setAttribute('hidden', '');
      // Reopen the grid overlay instead of closing everything
      if (this.gridInstance) {
        this.gridInstance.open();
      } else {
        // Only remove modal-open class if no other modals are open
        const openModals = document.querySelectorAll('.lookbook-grid-overlay:not([hidden]), .product-detail-modal:not([hidden]), [data-password-form-modal]:not([hidden])');
        if (openModals.length === 0) {
          document.body.style.overflow = '';
          document.body.classList.remove('modal-open');
          document.documentElement.classList.remove('modal-open');
        }
      }
    }, 300);
  }

  destroy() {
    if (this.handleEscape) {
      document.removeEventListener('keydown', this.handleEscape);
    }
  }
}

// Store instances by section ID for cleanup
const passwordPageInstances = new Map();

// Initialize password page components
// Make it globally available for inline scripts
window.initPasswordPage = function initPasswordPage(sectionElement) {
  // Shopify adds data-section-id via shopify_attributes
  const sectionId = sectionElement.dataset.sectionId || 'default';
  const instances = {
    countdownTimer: null,
    glitchyMarquee: null,
    lookbookCarousel: null,
    lookbookGrid: null,
    productDetailModal: null,
  };

  // Countdown timers (can be multiple - mobile and desktop)
  const countdownTimers = sectionElement.querySelectorAll('[data-countdown-timer]');
  if (countdownTimers.length > 0) {
    instances.countdownTimer = Array.from(countdownTimers).map(timer => new CountdownTimer(timer));
  }

  // Glitchy marquee
  const glitchyMarquee = sectionElement.querySelector('[data-glitchy-marquee]');
  if (glitchyMarquee) {
    instances.glitchyMarquee = new GlitchyMarquee(glitchyMarquee);
  }

  // Lookbook carousel
  const lookbookCarousel = sectionElement.querySelector('[data-lookbook-carousel]');
  if (lookbookCarousel) {
    instances.lookbookCarousel = new LookbookCarousel(lookbookCarousel);
  }

  // Lookbook grid
  const lookbookGrid = sectionElement.querySelector('[data-lookbook-grid]');
  if (lookbookGrid) {
    instances.lookbookGrid = new LookbookGrid(sectionElement);
  }

  // Product detail modal
  const productDetailModal = sectionElement.querySelector('[data-product-detail-modal]');
  if (productDetailModal) {
    instances.productDetailModal = new ProductDetailModal(sectionElement);
  }

  // Store instances for cleanup
  passwordPageInstances.set(sectionId, instances);
  return instances;
};

// Cleanup function
function cleanupPasswordPage(sectionId) {
  const instances = passwordPageInstances.get(sectionId);
  if (!instances) return;

  if (instances.countdownTimer) {
    // Handle both single timer and array of timers
    if (Array.isArray(instances.countdownTimer)) {
      instances.countdownTimer.forEach(timer => timer.destroy());
    } else {
    instances.countdownTimer.destroy();
    }
  }
  if (instances.glitchyMarquee) {
    instances.glitchyMarquee.destroy();
  }
  if (instances.lookbookCarousel) {
    instances.lookbookCarousel.destroy();
  }
  if (instances.lookbookGrid) {
    instances.lookbookGrid.destroy();
  }
  if (instances.productDetailModal) {
    instances.productDetailModal.destroy();
  }
  
  passwordPageInstances.delete(sectionId);
}

// Helper function to find section element from event
function findSectionElement(event) {
  // Event bubbles, so we need to find the section element
  // Shopify adds data-section-id attribute via shopify_attributes
  const sectionId = event.detail?.sectionId;
  if (!sectionId) {
    // Fallback: try to find by traversing up from event.target
    let element = event.target;
    while (element && element !== document.body) {
      if (element.dataset?.sectionType) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }
  
  // Find the section element by data-section-id
  const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
  if (sectionElement) return sectionElement;
  
  // Fallback: find by section type if ID doesn't work
  return event.target.closest('[data-section-type]');
}

// Password Form Modal Handler
function initPasswordFormModal(sectionElement) {
  const modal = sectionElement.querySelector('[data-password-form-modal]');
  const triggers = sectionElement.querySelectorAll('[data-password-form-trigger]');
  const closeBtn = sectionElement.querySelector('[data-password-form-close]');
  const modalContent = sectionElement.querySelector('[data-modal-content]');

  if (!modal) return null;

  const handlers = {
    open: () => {
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      // Focus the password input
      const passwordInput = modal.querySelector('input[type="password"]');
      if (passwordInput) {
        setTimeout(() => passwordInput.focus(), 100);
      }
    },
    close: () => {
      modal.setAttribute('hidden', '');
      // Only remove modal-open class if no other modals are open
      const openModals = document.querySelectorAll('.lookbook-grid-overlay:not([hidden]), .product-detail-modal:not([hidden]), [data-password-form-modal]:not([hidden])');
      if (openModals.length === 0) {
        document.body.style.overflow = '';
        document.body.classList.remove('modal-open');
        document.documentElement.classList.remove('modal-open');
      }
    },
    handleBackdropClick: (e) => {
      if (e.target === modal) {
        handlers.close();
      }
    },
    handleContentClick: (e) => {
      e.stopPropagation();
    },
    handleEscape: (e) => {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) {
        handlers.close();
      }
    }
  };

  // Add click handlers to all logo triggers (support both click and touch for mobile)
  triggers.forEach(trigger => {
    const handleOpen = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handlers.open();
    };
    
    trigger.addEventListener('click', handleOpen);
    // Add touch support for mobile
    trigger.addEventListener('touchend', (e) => {
      e.preventDefault();
      handleOpen(e);
    });
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', handlers.close);
  }

  modal.addEventListener('click', handlers.handleBackdropClick);

  if (modalContent) {
    modalContent.addEventListener('click', handlers.handleContentClick);
  }

  document.addEventListener('keydown', handlers.handleEscape);

  return handlers;
}


// Store instances by section ID for cleanup
const passwordPageInstancesMap = new Map();

// Initialize password page components for any section type
function initPasswordPageComponents(sectionElement) {
  const sectionId = sectionElement.dataset.sectionId || 'default';
  const sectionType = sectionElement.dataset.sectionType;
  
  // Clean up any existing instances for this section first
  cleanupPasswordPage(sectionId);
  
  const instances = {
    countdownTimers: [],
    passwordFormModal: null,
    lookbookGrid: null,
    productDetailModal: null,
    glitchyMarquee: null,
    lookbookCarousel: null,
  };
  
  // Initialize all countdown timers (mobile and desktop)
  const countdownTimers = sectionElement.querySelectorAll('[data-countdown-timer]');
  countdownTimers.forEach(timer => {
    if (timer) {
      instances.countdownTimers.push(new CountdownTimer(timer));
    }
  });
  
  // Initialize password form modal
  const passwordFormModal = initPasswordFormModal(sectionElement);
  if (passwordFormModal) {
    instances.passwordFormModal = passwordFormModal;
  }
  
  // Initialize lookbook grid and product detail modal for password, generic, and upcoming sections
  if (sectionType === 'password' || sectionType === 'generic-password-page' || sectionType === 'upcoming-password-page') {
    const lookbookGrid = sectionElement.querySelector('[data-lookbook-grid]');
    if (lookbookGrid) {
      instances.lookbookGrid = new LookbookGrid(sectionElement);
    }
    
    const productDetailModal = sectionElement.querySelector('[data-product-detail-modal]');
    if (productDetailModal) {
      instances.productDetailModal = new ProductDetailModal(sectionElement);
    }
  }
  
  // Initialize full password page components (for password section type)
  if (sectionType === 'password') {
    const passwordPageInstances = initPasswordPage(sectionElement);
    if (passwordPageInstances) {
      // Merge password page instances
      if (passwordPageInstances.countdownTimer) {
        if (Array.isArray(passwordPageInstances.countdownTimer)) {
          instances.countdownTimers.push(...passwordPageInstances.countdownTimer);
        } else {
          instances.countdownTimers.push(passwordPageInstances.countdownTimer);
        }
      }
      instances.glitchyMarquee = passwordPageInstances.glitchyMarquee;
      instances.lookbookCarousel = passwordPageInstances.lookbookCarousel;
    }
  }
  
  // Store instances for cleanup
  passwordPageInstancesMap.set(sectionId, instances);
  return instances;
}

// Cleanup function
function cleanupPasswordPage(sectionId) {
  const instances = passwordPageInstancesMap.get(sectionId);
  if (!instances) return;

  // Clean up countdown timers
  if (instances.countdownTimers && instances.countdownTimers.length > 0) {
    instances.countdownTimers.forEach(timer => {
      if (timer && timer.destroy) {
        timer.destroy();
      }
    });
  }
  
  // Clean up password form modal
  if (instances.passwordFormModal && instances.passwordFormModal.destroy) {
    instances.passwordFormModal.destroy();
  }
  
  // Clean up lookbook grid
  if (instances.lookbookGrid && instances.lookbookGrid.destroy) {
    instances.lookbookGrid.destroy();
  }
  
  // Clean up product detail modal
  if (instances.productDetailModal && instances.productDetailModal.destroy) {
    instances.productDetailModal.destroy();
  }
  
  // Clean up glitchy marquee
  if (instances.glitchyMarquee && instances.glitchyMarquee.destroy) {
    instances.glitchyMarquee.destroy();
  }
  
  // Clean up lookbook carousel
  if (instances.lookbookCarousel && instances.lookbookCarousel.destroy) {
    instances.lookbookCarousel.destroy();
  }
  
  passwordPageInstancesMap.delete(sectionId);
}

// Helper function to find section element from event (kept for backward compatibility if needed)
// Note: Following Shopify guide pattern, we now use event.target directly in event listeners
function findSectionElement(event) {
  // Try to find by section ID first
  const sectionId = event.detail?.sectionId;
  if (sectionId) {
    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (section) return section;
  }
  
  // Fallback: find by section type if ID doesn't work
  return event.target.closest('[data-section-type]');
}

// Parse URL parameters
function getURLParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    page: params.get('page'),
    product: params.get('product')
  };
}

// Handle URL parameters to open lookbook grid and/or product detail
function handleURLParams(sectionElement) {
  const urlParams = getURLParams();
  
  if (urlParams.page === 'lookbook') {
    const sectionId = sectionElement.dataset.sectionId || 'default';
    const instances = passwordPageInstancesMap.get(sectionId);
    
    if (!instances) return;
    
    // Open lookbook grid
    if (instances.lookbookGrid) {
      // Small delay to ensure grid is fully initialized
      setTimeout(() => {
        instances.lookbookGrid.open();
        
        // If product parameter is present, open that product detail
        if (urlParams.product && instances.productDetailModal) {
          // Find product by ID or handle
          const productId = urlParams.product;
          const productItem = sectionElement.querySelector(
            `[data-product-id="${productId}"], [data-product-handle="${productId}"]`
          );
          
          if (productItem) {
            const productHandle = productItem.dataset.productHandle || productId;
            const foundProductId = productItem.dataset.productId || productId;
            
            // Set grid instance reference so grid reopens when product modal closes
            instances.productDetailModal.gridInstance = instances.lookbookGrid;
            
            // Wait for grid to open, then open product detail
            setTimeout(() => {
              instances.productDetailModal.loadProduct(foundProductId, productHandle);
            }, 400); // Wait for grid animation to start
          } else {
            console.warn('[URL Params] Product not found:', productId);
          }
        }
      }, 100);
    }
  }
}

// Initialize on DOM ready (for normal page loads)
document.addEventListener('DOMContentLoaded', () => {
  // Initialize password section
  const passwordSection = document.querySelector('[data-section-type="password"]');
  if (passwordSection) {
    initPasswordPageComponents(passwordSection);
    handleURLParams(passwordSection);
  }
  
  // Initialize generic password page
  const genericPasswordSection = document.querySelector('[data-section-type="generic-password-page"]');
  if (genericPasswordSection) {
    initPasswordPageComponents(genericPasswordSection);
    handleURLParams(genericPasswordSection);
  }
  
  // Initialize upcoming password page
  const upcomingPasswordSection = document.querySelector('[data-section-type="upcoming-password-page"]');
  if (upcomingPasswordSection) {
    initPasswordPageComponents(upcomingPasswordSection);
    handleURLParams(upcomingPasswordSection);
  }
});

// Shopify theme editor event listeners
// Following Shopify's recommended pattern from theme-editor.mdc
document.addEventListener('shopify:section:load', (event) => {
  // Check if this is a password page section
  const sectionType = event.target.dataset.sectionType;
  if (event.detail.sectionId && (sectionType === 'password' || sectionType === 'generic-password-page' || sectionType === 'upcoming-password-page')) {
    // Use event.target directly as per Shopify guide pattern
    initPasswordPageComponents(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const sectionType = event.target.dataset.sectionType;
  if (event.detail.sectionId && (sectionType === 'password' || sectionType === 'generic-password-page' || sectionType === 'upcoming-password-page')) {
    cleanupPasswordPage(event.detail.sectionId);
  }
});

// Also listen for section:select in case load doesn't fire
document.addEventListener('shopify:section:select', (event) => {
  const sectionType = event.target.dataset.sectionType;
  if (event.detail.sectionId && event.detail.load && (sectionType === 'password' || sectionType === 'generic-password-page' || sectionType === 'upcoming-password-page')) {
    initPasswordPageComponents(event.target);
  }
});

